<template>
  <TabPanel class="tw-w-full">
    <div class="tw-space-y-5">
      <div v-for="[key, field] in fieldsList" :key="key" class="tw-grid tw-grid-cols-12 tw-items-end tw-gap-5">
        <div class="tw-col-span-full md:tw-col-span-5">
          <Text v-if="baseDescription(field)" size="small" class="tw-mb-2 tw-text-white/68">
            {{ baseDescription(field) }}
          </Text>

          <FormField
            v-if="field.type === 'select'"
            type="select"
            v-model="modelsByKey[key]"
            :label="field.label || key"
          >
            <option v-for="opt in field.options || []" :key="opt.id" :value="opt.id">
              {{ opt.label }}
            </option>
          </FormField>

          <Checkbox
            v-else-if="field.type === 'checkbox'"
            v-model="modelsByKey[key]"
            :right-label="field.label || key"
          />

          <FormField v-else-if="field.type === 'text'" v-model="modelsByKey[key]" :label="field.label || key" />
        </div>

        <div class="tw-col-span-full md:tw-col-span-7">
          <Text v-if="dynamicDescriptionByKey[key]" class="tw-text-white/89">
            {{ dynamicDescriptionByKey[key] }}
          </Text>
        </div>
      </div>

      <Button v-if="isChanged" color="semiTransparentWhite" class="tw-w-fit" @click="resetToDefaults">
        Reset to defaults
      </Button>

      <div
        v-if="decimator"
        class="tw-grid tw-grid-cols-12 tw-items-center tw-gap-5 tw-border-t tw-border-purple/25 tw-pt-5"
      >
        <div class="tw-col-span-full md:tw-col-span-5">
          <Button color="semiTransparentWhite" class="tw-w-full tw-justify-center" @click="decimatorOpen = true">
            {{ t('decimator.open') }}
          </Button>
        </div>

        <div class="tw-col-span-full md:tw-col-span-7">
          <Text class="tw-text-white/89">{{ decimatorSummary }}</Text>

          <Text v-if="decimatorChanges.length" class="tw-font-black tw-text-orange">
            {{ t('decimator.changed', { changes: decimatorChanges.join(', ') }) }}
          </Text>
        </div>
      </div>
    </div>
  </TabPanel>
  <DecimatorModal v-model="decimatorOpen" />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button, Checkbox, FormField, TabPanel, Text } from '@slideslive/fuse-kit/vue';

import useStateStore from '@/stores/state';
import { normalizeWiringValue } from '@/utils/sidebar_status';

import DecimatorModal from '../DecimatorModal.vue';

const scope = 'production';

const { t } = useI18n();

const stateStore = useStateStore();
const { state, wiringDraftProduction } = storeToRefs(stateStore);
const { setWiringDraftValue, resetWiringDraftToDefaults } = stateStore;

const fields = computed(() => state.value?.data?.wiring?.fields?.[scope] || {});
const fieldsList = computed(() => Object.entries(fields.value || {}));

const modelsByKey = reactive({});

const createModelForKey = (key) =>
  computed({
    get() {
      const field = fields.value?.[key];
      const rawDraft = wiringDraftProduction.value?.[key];

      if (rawDraft !== undefined) return normalizeWiringValue(field, rawDraft);

      const rawRemote = field?.value ?? field?.default ?? '';
      return normalizeWiringValue(field, rawRemote);
    },
    set(value) {
      const field = fields.value?.[key];
      const next = normalizeWiringValue(field, value);
      setWiringDraftValue(scope, key, next);
    },
  });

watch(
  fields,
  (nextFields) => {
    const group = nextFields || {};

    Object.keys(group).forEach((key) => {
      if (!modelsByKey[key]) {
        modelsByKey[key] = createModelForKey(key);
      }
    });
  },
  { immediate: true },
);

function baseDescription(field) {
  return String(field?.description || '').trim();
}

const dynamicDescriptionByKey = computed(() => {
  const out = {};
  const group = fields.value || {};
  const draft = wiringDraftProduction.value || {};

  Object.entries(group).forEach(([key, field]) => {
    if (field?.type !== 'select') {
      out[key] = '';
      return;
    }

    const raw = draft[key] !== undefined ? draft[key] : (field?.value ?? field?.default ?? '');
    const id = String(raw ?? '').trim();
    if (!id) {
      out[key] = '';
      return;
    }

    const opts = Array.isArray(field?.options) ? field.options : [];
    const hit = opts.find((o) => String(o?.id ?? '').trim() === id) || null;

    out[key] = String(hit?.description || '').trim();
  });

  return out;
});

const isChanged = computed(() => {
  const group = fields.value || {};
  const draft = wiringDraftProduction.value || {};

  return Object.entries(group).some(([key, field]) => {
    const draftVal = normalizeWiringValue(field, draft[key]);
    const defaultVal = normalizeWiringValue(field, field?.default);
    return draftVal !== defaultVal;
  });
});

const resetToDefaults = (event) => {
  if (event) event.preventDefault();
  resetWiringDraftToDefaults(scope);
};

// Older nate-runner versions have no Decimator control and do not send this field.
const decimator = computed(() => state.value?.data?.decimator);
const decimatorOpen = ref(false);

function decimatorValueLabel(setting) {
  if (setting.type === 'checkbox') return setting.value ? t('decimator.on') : t('decimator.off');
  return setting.options?.find((option) => option.id === setting.value)?.label || setting.value;
}

// Settings that differ from NATEBox defaults, e.g. "Scaler source: HDMI in".
const decimatorChanges = computed(() =>
  Object.values(decimator.value?.settings || {})
    .filter((setting) => setting.natebox_default != null && setting.value !== setting.natebox_default)
    .map((setting) => `${setting.label}: ${decimatorValueLabel(setting)}`),
);

const decimatorSummary = computed(() => {
  if (!decimator.value?.connected) return t('decimator.summary_not_connected');

  const scalerSource = decimator.value.settings?.DUC_Source;
  const scalerSourceLabel = scalerSource?.options?.find((option) => option.id === scalerSource.value)?.label;

  return t('decimator.summary', {
    serial: decimator.value.serial,
    settings: decimator.value.has_natebox_defaults ? t('decimator.natebox_defaults') : t('decimator.custom_settings'),
    source: scalerSourceLabel || '-',
  });
});
</script>
